import React, { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { getUsers } from '../../data/mockData';
import UserToolbar from './UserToolbar';
import UserTable from './UserTable';
import UserFormModal from './UserFormModal';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import FloatingActionButton from '../shared/FloatingActionButton';
import BlockUserModal from './BlockUserModal';
import { ENDPOINTS } from '../../constant/ApiEndpoints';
import api from '../../services/ApiService';
import useDidFetch from '../../custom_hooks/useDidfetch';
import { ROLE } from '../../constant/roles';
import MemberRegistrationWizard from '../registration/MemberRegistrationWizard';
import { cleanObject } from '../CleanObject';

const AllUsersTab = ({ isShowFilter, isMembers }) => {
    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [filters, setFilters] = useState({ search: '', role: 'All', status: 'All', plans: "All" });
    const [isLoading, setIsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isReceptionist, setIsReceptionist] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [actionUser, setActionUser] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null); // 'delete', 'block', 'unblock'

    const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);

    const handleAddUser = () => {
        setEditingUser(null);
        setIsFormModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setIsFormModalOpen(true);
    };

    const handleDeleteUser = (user) => {
        setActionUser(user);
        setConfirmAction('delete');
        setIsConfirmOpen(true);
    };

    const handleBlockUser = (user) => {
        setActionUser(user);
        if (user.status === 'Blocked') {
            setConfirmAction('unblock');
            setIsConfirmOpen(true);
            return;
        }

        const latestPlan = user.planHistory?.[0];
        if (latestPlan?.status !== 'Active') {
            setConfirmAction('block');
            setIsConfirmOpen(true);
        } else {
            setIsBlockModalOpen(true);
        }
    };

    const confirmActionHandler = async (reason) => {
        if (!actionUser) return;

        if (confirmAction === 'delete') {
            try {
                setIsLoading(true);
                const response = await api.delete(`${ENDPOINTS.deleteUser}/${actionUser.id}`);
                const result = response?.data;
                console.log(response);
                if (response.status === 200) {
                    getAllUser();
                    toast.success(`User "${actionUser.name}" has been removed.`);
                }
            } catch (error) {
                console.error("user delete error:", error);
            }
        }
        // Reset states
        setIsConfirmOpen(false);
        setIsBlockModalOpen(false);
        setActionUser(null);
        setConfirmAction(null);
    };

    const runOnce = useDidFetch();
    useEffect(() => {
        runOnce(() => {
            const storedUser = localStorage.getItem("user");
            const user = storedUser ? JSON.parse(storedUser) : null;
            if (ROLE[user?.role_id] != 'receptionist') {
                getAllUser();
                setIsReceptionist(true);
            } else {
                getMembers();
                getPlans()
            }
        })
    }, []);
    const getPlans = async () => {
        try {
            const response = await api.get(`${ENDPOINTS.getPlan}`);
            const result = response?.data;
            console.log(response);
            if (response.status === 200) {
                setPlans(result ?? []);
            }
        } catch (error) {
            console.error("user get error:", error);
        }
    }
    const getMembers = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`${ENDPOINTS.getUserByRole}/member`);
            const result = response?.data;
            console.log(response);
            if (response.status === 200) {
                setUsers(result?.users ?? []);
            }
        } catch (error) {
            console.error("user get error:", error);
        } finally {
            setIsLoading(false);
        }
    }
    const getAllUser = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(ENDPOINTS.getUser);
            const result = response?.data;
            console.log(response);
            if (response.status === 200) {
                setUsers(result?.users ?? []);
            }
        } catch (error) {
            console.error("user get error:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSaveUser = async (userData) => {
        console.log(userData);
        userData = { ...userData, ...userData?.meta };
        userData['role'] = parseInt(userData?.role);
        userData['role_id'] = parseInt(userData?.role);
        userData = cleanObject(userData);
        try {
            setSaveLoading(true);
            const response = editingUser ? await api.put(`${ENDPOINTS.updateUser}/${userData?.id}`, userData) : await api.post(ENDPOINTS.saveUser, userData);
            const result = response?.data;
            if (response.status === 200 || response.status === 201) {
                toast.success(result?.message || "User has been Created!");
                setIsFormModalOpen(false);
                setEditingUser(null);
                isMembers ? getMembers() : getAllUser();
            } else {
                console.error("else---->>>>> ", error);
                toast.error(result?.message);
            }
        } catch (error) {
            // toast.error(error?.response?.data?.error);
            console.error("save || Edit user ", error);
        } finally {
            setSaveLoading(false);
        }
    };
    const handleRegistrationSuccess = async (newMember) => {

        let trainerId = parseInt(newMember?.membership?.assignedTrainer ?? '0');
        let plan_id = parseInt(newMember?.membership?.planId ?? '0');
        let obj = {
            //personal
            "name": newMember?.personal?.name,
            "email": newMember?.personal?.email,
            "phone": newMember?.personal?.contact,
            "dob": newMember?.personal?.dob,
            "gender": newMember?.personal?.gender,
            "emergency_contact_name": newMember?.personal?.emergencyContactName,
            "emergency_contact_number": newMember?.personal?.emergencyContactPhone,
            "address": newMember?.personal?.address,
            //health
            "height_cm": newMember?.health?.height,
            "weight_kg": newMember?.health?.weight,
            "fitness_level": newMember?.health?.level,
            "medical_conditions": newMember?.health?.conditions,
            "fitness_goals": newMember?.health?.goals,
            //membership
            "role_id": 3,
            "role": 3,
            "trainer_assigned": trainerId,
            "plan_id": plan_id,
            "image_base64": newMember?.personal?.base64Img,
            "start_date": newMember?.membership?.startDate,
            "end_date": newMember?.membership?.endDate,
            "status": newMember?.membership?.userStatus
        };
        if (editingUser) {
            const oldPlan = editingUser?.plan?.id;
            const newPlan = obj?.plan_id; 
                console.log(`oldPlan ${oldPlan} - ${newPlan}`);
            if (!newPlan || String(oldPlan) === String(newPlan)) { 
                delete obj.plan_id;
                console.log(obj);
            }
        }
        console.log(editingUser?.plan?.id);
        let memberData = cleanObject(obj);
        console.log(memberData?.plan_id)
        try {
            setSaveLoading(true);
            const response = await api.put(`${ENDPOINTS.updateUser}/${newMember.id}`, memberData);
            const result = response?.data;
            console.log(response);
            if (response?.status == 200 || response?.status == 201) {
                toast.success(result?.message ?? "Member saved Successfully");
                setIsFormModalOpen(false);
                isMembers ? getMembers() : getAllUser();
            }
        } catch (error) {
            console.log("new member registration", error);
        } finally {
            setSaveLoading(false);
        }

    };

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const searchLower = filters.search.toLowerCase();
            const nameMatch = user.name.toLowerCase().includes(searchLower);
            const emailMatch = user.email.toLowerCase().includes(searchLower);
            const roleMatch = filters.role === 'All' || user.role?.name === filters.role;
            const statusMatch = filters.status === 'All' || user.status === filters.status;
            const planMatch = filters.plans === 'All' || user?.plan?.id == filters.plans;
            return (nameMatch || emailMatch) && roleMatch && statusMatch && planMatch;
        });
    }, [users, filters]);

    const getConfirmationDialogProps = () => {
        switch (confirmAction) {
            case 'delete':
                return { title: 'Confirm Deletion', description: `Are you sure you want to remove ${actionUser?.name}? This action cannot be undone.` };
            case 'block':
                return { title: 'Confirm Block', description: `Are you sure you want to block ${actionUser?.name}?` };
            case 'unblock':
                return { title: 'Confirm Unblock', description: `Are you sure you want to unblock ${actionUser?.name}?` };
            default:
                return { title: '', description: '' };
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>Total {isMembers ? 'Members' : 'Users'} : {filteredUsers.length}</div>
                <UserToolbar filters={filters} onFilterChange={setFilters} isShowFilter={isShowFilter} isMembers={isMembers} plans={plans} />
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <UserTable
                    role={isReceptionist}
                    users={filteredUsers}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                    onBlock={handleBlockUser}
                    isMembers={isMembers}
                />
            </div>
            {isReceptionist &&
                <FloatingActionButton onClick={handleAddUser}>
                    <Plus size={24} />
                </FloatingActionButton>
            }
            {isReceptionist &&
                <UserFormModal
                    isLoading={saveLoading}
                    isOpen={isFormModalOpen}
                    onClose={() => setIsFormModalOpen(false)}
                    onSave={handleSaveUser}
                    user={editingUser}
                />
            }

            <ConfirmationDialog
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={confirmActionHandler}
                {...getConfirmationDialogProps()}
            />

            <BlockUserModal
                isOpen={isBlockModalOpen}
                onClose={() => setIsBlockModalOpen(false)}
                onConfirm={(reason) => confirmActionHandler(reason)}
                userName={actionUser?.name}
            />
            {!isReceptionist &&
                <MemberRegistrationWizard
                    confirmBtnLoading={saveLoading}
                    isOpen={isFormModalOpen}
                    onClose={() => setIsFormModalOpen(false)}
                    onSuccess={handleRegistrationSuccess}
                    initialData={editingUser}
                />
            }
        </div>
    );
};

export default AllUsersTab;
